import React from 'react';
import {
    Stack,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
} from '@mui/material';

interface RecursoFinanceiro {
    tipo: string;
    descricao: string;
    valor_orcado: number;
    valor_liquidado: number;
}

interface ViewFinancialResourceProps {
    recursos: RecursoFinanceiro[];
    rcl: number;
}

const formatarValorParaExibicao = (valor: number | string | null | undefined): string => {
    if (!valor && valor !== 0) return '';
    const numeroValor = typeof valor === 'string' ? parseFloat(valor as string) : valor;
    return numeroValor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getRecursoKey = (recurso: RecursoFinanceiro, index: number) => {
    const baseKey = `${recurso.tipo}-${recurso.descricao}`.replace(/\s+/g, '-').toLowerCase();
    return `${baseKey}-${index}`;
};

const ViewFinancialResource: React.FC<ViewFinancialResourceProps> = ({ recursos, rcl }) => {
    const totalOrcado = recursos.reduce((acc, item) => acc + (item.valor_orcado || 0), 0);
    const totalLiquidado = recursos.reduce((acc, item) => acc + (item.valor_liquidado || 0), 0);
    const percentualExecucao = totalOrcado > 0 ? (totalLiquidado / totalOrcado) * 100 : 0;
    const percentualInvestidoRCL = rcl > 0 ? (totalLiquidado / rcl) * 100 : 0;

    return (
        <Stack marginX={{ xs: '1rem', md: '4rem' }} spacing='2rem' maxWidth='1152px' width='100%'>
            <Typography variant="h5" fontWeight="bold">
                Visualização dos Recursos Financeiros Aplicados
            </Typography>

            <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" mb={2}>Itens de Recursos Financeiros</Typography>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><b>Tipo</b></TableCell>
                                <TableCell><b>Descrição</b></TableCell>
                                <TableCell><b>Valor Orçado (R$)</b></TableCell>
                                <TableCell><b>Valor Liquidado (R$)</b></TableCell>
                                <TableCell><b>% Execução</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {recursos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Nenhum recurso cadastrado.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                recursos.map((item, idx) => {
                                    const percExec = item.valor_orcado > 0
                                        ? (item.valor_liquidado / item.valor_orcado) * 100
                                        : 0;
                                    return (
                                        <TableRow key={getRecursoKey(item, idx)}>
                                            <TableCell>{item.tipo}</TableCell>
                                            <TableCell>{item.descricao}</TableCell>
                                            <TableCell>R$ {formatarValorParaExibicao(item.valor_orcado)}</TableCell>
                                            <TableCell>R$ {formatarValorParaExibicao(item.valor_liquidado)}</TableCell>
                                            <TableCell>{percExec.toFixed(2)}%</TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="body1" fontWeight="bold" mb={1}>
                    Valor total da Receita Corrente Líquida do município no ano de 2025
                </Typography>
                <Typography variant="h6">
                    R$ {formatarValorParaExibicao(rcl)}
                </Typography>
            </Paper>

            <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" mb={2}>Resumo Financeiro</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', textAlign: 'center' }}>
                            <Typography variant="body2">Total Orçado</Typography>
                            <Typography variant="h6" fontWeight="bold">
                                R$ {formatarValorParaExibicao(totalOrcado)}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Paper sx={{ p: 2, backgroundColor: '#e8f5e9', textAlign: 'center' }}>
                            <Typography variant="body2">Total Liquidado</Typography>
                            <Typography variant="h6" fontWeight="bold">
                                R$ {formatarValorParaExibicao(totalLiquidado)}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Paper sx={{ p: 2, backgroundColor: '#fff3e0', textAlign: 'center' }}>
                            <Typography variant="body2">% Execução</Typography>
                            <Typography variant="h6" fontWeight="bold">
                                {percentualExecucao.toFixed(2)}%
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Paper sx={{ p: 2, backgroundColor: '#f3e5f5', textAlign: 'center' }}>
                            <Typography variant="body2">% Investido da RCL</Typography>
                            <Typography variant="h6" fontWeight="bold">
                                {percentualInvestidoRCL.toFixed(2)}%
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>
        </Stack>
    );
};

export default ViewFinancialResource;
